import { z } from 'zod';
import { jsonObject, timestamp, uuid } from './common.js';
import { listQueryInput, paginated } from './list.js';
import { previewDescriptor } from './preview.js';
import { userAlertSchema } from './errors.js';
/**
 * Files / ingest (§4, E10). `files`, `uploads`, `drive_connections`.
 */
/** Role of a stored object. One source → derivative `thumbnail`/`render` rows. */
export const fileKind = z.enum([
    'upload',
    'thumbnail',
    'render',
    'illustration',
    'cover',
    'bundle',
    'source', // ingested corpus text
]);
export const fileSchema = z.object({
    id: uuid,
    ownerId: uuid.nullable(), // null for platform/seed assets
    kind: fileKind,
    /** S3 object key within `xdocs-uploads`. */
    s3Key: z.string().min(1),
    contentType: z.string().min(1).max(255),
    byteSize: z.number().int().nonnegative(),
    /** Set for images/renders — drives CLS-safe dimensions in previews. */
    width: z.number().int().positive().nullable(),
    height: z.number().int().positive().nullable(),
    /** Derivative link (e.g. an upload's generated thumbnail). */
    thumbnailFileId: uuid.nullable(),
    /** Explorer placement (F7) — null = root. */
    folderId: uuid.nullable(),
    meta: jsonObject.nullable(),
    createdAt: timestamp,
});
/** Ingest lifecycle for a user upload (F18). */
export const uploadStatus = z.enum(['pending', 'uploaded', 'ingesting', 'ready', 'failed']);
/** Accepted source document MIME types (server re-validates before ingest). */
export const uploadMimeType = z.enum([
    'text/plain',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);
/** Direct F18 upload vs F6 Drive import — same `ingest-worker` pipeline. */
export const uploadSource = z.enum(['upload', 'drive']);
/**
 * Hard cap on a single uploaded file (F18 "oversize file limit" — FEATURES.md
 * names the edge case but pins no number; this is the single edit point).
 * MUST also be wired as `@fastify/multipart`'s `limits.fileSize` so the
 * server aborts the stream early, never buffering an oversize file to check
 * it after the fact (§15 "type/size-checked before ingest job runs").
 */
export const UPLOAD_MAX_BYTES = 25 * 1024 * 1024; // 25 MB
/** Guard against an unbounded multipart batch in one `POST /upload` (F18 "concurrent uploads"). */
export const UPLOAD_MAX_FILES_PER_REQUEST = 10;
export const uploadSchema = z.object({
    id: uuid,
    ownerId: uuid,
    fileId: uuid.nullable(), // set once the object lands
    originalFilename: z.string().min(1).max(500),
    contentType: uploadMimeType,
    byteSize: z.number().int().nonnegative(),
    status: uploadStatus,
    source: uploadSource,
    /** Set only when `source==='drive'`. */
    driveConnectionId: uuid.nullable(),
    /** The Drive-native remote file id (F6) — null for direct uploads. */
    driveFileId: z.string().nullable(),
    /** sha256 hex of the bytes, set once landed — F6 "dedupe by checksum". */
    checksum: z.string().nullable(),
    /** Target explorer folder (F7); null = root. */
    folderId: uuid.nullable(),
    /** Document created from this upload once ingested. */
    documentId: uuid.nullable(),
    error: z.string().nullable(),
    createdAt: timestamp,
    updatedAt: timestamp,
});
export const driveProvider = z.enum(['google_drive']);
export const driveConnectionSchema = z.object({
    id: uuid,
    userId: uuid,
    provider: driveProvider,
    /** Encrypted at rest (AES-GCM, key from k8s secret) — never leaves the server. */
    accessTokenEnc: z.string(),
    refreshTokenEnc: z.string().nullable(),
    scope: z.string().nullable(),
    expiresAt: timestamp.nullable(),
    createdAt: timestamp,
    updatedAt: timestamp,
});
// --- explorer folders (F7) ----------------------------------------------------
//
// Not in the plan's §4 entity table (FEATURES.md's F7 entity list — "files,
// documents, cache_entries" — is a UX-level summary, not the physical
// schema). A dedicated `folders` table/schema is used here rather than
// overloading `fileKind` with a `'folder'` value, so `fileSchema`'s existing
// required `s3Key`/`contentType`/`byteSize` never has to go nullable for a
// row with no bytes. `parentFolderId: null` = explorer root.
export const folderSchema = z.object({
    id: uuid,
    ownerId: uuid,
    parentFolderId: uuid.nullable(),
    name: z.string().min(1).max(255),
    createdAt: timestamp,
    updatedAt: timestamp,
    deletedAt: timestamp.nullable(),
});
const folderNameField = z
    .string()
    .trim()
    .min(1, 'errors:explorer.name.required')
    .max(255, 'errors:explorer.name.tooLong');
export const folderCreateInput = z.object({
    name: folderNameField,
    /** Parent to create under; null = root. */
    parentFolderId: uuid.nullable().default(null),
});
export const folderRenameInput = z.object({ folderId: uuid, name: folderNameField });
/**
 * F7 "block move into a descendant folder" — the trivial self-move case
 * (`folderId === parentFolderId`) is rejected here by construction; the full
 * ancestor-chain check (a folder can't move under its own descendant) needs
 * the DB tree and is a server-side `FORBIDDEN`/`CONFLICT` check, not zod's job.
 */
export const folderMoveInput = z
    .object({ folderId: uuid, parentFolderId: uuid.nullable() })
    .refine((v) => v.parentFolderId !== v.folderId, {
    message: 'errors:explorer.move.intoSelf',
    path: ['parentFolderId'],
});
export const folderDeleteInput = z.object({ folderId: uuid });
export const folderDeleteResult = z.object({ folderId: uuid, deleted: z.boolean() });
// --- explorer listing (F7 tree/breadcrumb semantics) --------------------------
/** Display-level provenance badge (F7 AC) — distinct from `uploadSource`, which
 *  only exists for ingest records; a `'generated'` file has no `uploads` row. */
export const explorerProvenance = z.enum(['uploaded', 'drive', 'generated']);
const explorerEntryCommon = {
    id: uuid,
    name: z.string().min(1),
    ownerId: uuid,
    /** Current parent; null = root. */
    folderId: uuid.nullable(),
    createdAt: timestamp,
    updatedAt: timestamp,
};
export const explorerFolderEntry = z.object({
    type: z.literal('folder'),
    ...explorerEntryCommon,
});
export const explorerFileEntry = z.object({
    type: z.literal('file'),
    ...explorerEntryCommon,
    kind: fileKind,
    contentType: z.string(),
    byteSize: z.number().int().nonnegative(),
    provenance: explorerProvenance,
    /** Ingest lifecycle (F7 "processing/error" UI states); 'ready' for files with no `uploads` row. */
    status: uploadStatus,
    /** `uploads.parse_error`, human-readable (E12) — set only when `status==='failed'`. */
    errorMessage: z.string().nullable(),
    preview: previewDescriptor.nullable(),
    /** Set when this file backs/derives from a generated document. */
    documentId: uuid.nullable(),
});
/** A single row of `file.list` — either a folder or a file (F7 combined grid/list). */
export const explorerEntry = z.discriminatedUnion('type', [explorerFolderEntry, explorerFileEntry]);
export const explorerSortColumns = ['name', 'createdAt', 'updatedAt', 'byteSize'];
/** `file.list` — folder-scoped, paginated/sorted/searched-within (§5.9). */
export const fileListQuery = listQueryInput(explorerSortColumns, {
    defaultSort: 'name',
    defaultDir: 'asc',
}).extend({
    /** The folder being browsed; null = root. */
    folderId: uuid.nullable().default(null),
    /**
     * Server-side row-type filter (§5.9 "fetch-all-then-filter is a review
     * block") — e.g. the F7 move picker only ever wants folders. Omitted =
     * both types, the existing combined explorer listing behavior.
     */
    entryType: z.enum(['file', 'folder']).optional(),
});
export const breadcrumbEntry = z.object({ id: uuid, name: z.string() });
/** `file.list` response — the paginated envelope plus the root→current breadcrumb chain. */
export const fileListResult = paginated(explorerEntry).extend({
    folderId: uuid.nullable(),
    breadcrumbs: z.array(breadcrumbEntry),
});
export const fileGetInput = z.object({ fileId: uuid });
/**
 * `file.get` response (F7 preview pane) — the row plus a short-lived
 * presigned GET url (E10 §9 "presigned GET/PUT via the MinIO client"; §5.8
 * "large artifacts are never sent over WS — push a presigned URL"). Added by
 * WS-FILES (F18/F7 server slice) — `fileGetInput` alone had no paired result
 * schema yet.
 */
export const fileGetResult = z.object({ file: fileSchema, downloadUrl: z.string().url() });
export const fileDeleteInput = z.object({ fileId: uuid });
export const fileDeleteResult = z.object({ fileId: uuid, deleted: z.boolean() });
const fileNameField = z
    .string()
    .trim()
    .min(1, 'errors:explorer.name.required')
    .max(500, 'errors:explorer.name.tooLong');
export const fileRenameInput = z.object({ fileId: uuid, name: fileNameField });
export const fileMoveInput = z.object({ fileId: uuid, folderId: uuid.nullable() });
/**
 * Live push on `user:{userId}` (§5.5) whenever an explorer entry changes —
 * "signal, not payload" like `documentVersionPush`/`jobProgressPush`; the
 * client refetches the affected `file.list` page on receipt. E1 write-site
 * cache invalidation (`invalidateTag('user:{userId}')`) and this push are
 * the two halves of one write.
 */
export const explorerEventPush = z.object({
    event: z.enum(['created', 'renamed', 'moved', 'deleted', 'processing', 'ready', 'failed']),
    entryType: z.enum(['file', 'folder']),
    id: uuid,
    /** Current (post-event) parent — lets the client invalidate both the old and new folder pages. */
    folderId: uuid.nullable(),
});
// --- REST `POST /upload` (multipart, F18 — the one non-WS ingest entry) -------
/** The non-file multipart fields alongside the file part(s). */
export const uploadRequestMeta = z.object({
    /** Target explorer folder; omitted/null = root. */
    folderId: uuid.nullable().optional(),
});
/** Per-file outcome — a multi-file drop can partially succeed (F18 AC). */
export const uploadFileResult = z.discriminatedUnion('ok', [
    z.object({ ok: z.literal(true), upload: uploadSchema }),
    z.object({ ok: z.literal(false), filename: z.string(), error: userAlertSchema }),
]);
export const uploadResponse = z.object({ files: z.array(uploadFileResult) });
/**
 * The one MIME/size gate `POST /upload` runs BEFORE the ingest job (and
 * before fully reading the stream — pair with `@fastify/multipart`
 * `limits.fileSize: UPLOAD_MAX_BYTES` so an oversize file is aborted
 * mid-stream, never buffered first). Returns `null` when the file passes.
 */
export function validateUploadConstraints(input) {
    const mimeCheck = uploadMimeType.safeParse(input.contentType);
    if (!mimeCheck.success) {
        return {
            code: 'VALIDATION',
            message: 'errors:upload.mimeType.unsupported',
            fields: [{ path: 'contentType', message: 'errors:upload.mimeType.unsupported' }],
        };
    }
    if (input.byteSize > UPLOAD_MAX_BYTES) {
        return {
            code: 'VALIDATION',
            message: 'errors:upload.size.tooLarge',
            fields: [{ path: 'byteSize', message: 'errors:upload.size.tooLarge' }],
        };
    }
    return null;
}
